<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>友だち追加経路別初期チャットメッセージ編集</template>
      <template #right>
        <div :class="$style.button">
          <FlatButton
            variant="caution"
            :disabled="isDeleting"
            @click="handleDelete"
          >
            削除
          </FlatButton>
          <FlatButton
            :class="$style.button"
            :disabled="isSubmitDisabled"
            @click="handleSubmit"
          >
            保存
          </FlatButton>
        </div>
      </template>
    </SettingsPagePanelHeader>

    <div :class="$style.introductionMessageForm">
      <IntroductionMessageConditionForm
        v-if="inflowSourceOptions && conditionForm"
        v-model="conditionForm"
        :inflowSourceOptions="inflowSourceOptions"
      />

      <IntroductionMessageContentForm
        v-if="
          contentForm &&
          result?.viewer.hasLineActivationProvider != null &&
          scenarioChatOptions
        "
        v-model="contentForm"
        :class="$style.introductionMessageForm__messageContent"
        :scenarioChatOptions="scenarioChatOptions"
        :hasLineActivationProvider="result.viewer.hasLineActivationProvider"
      />
    </div>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useAlert } from '@/libs/alertPortal'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import { captureException } from '@sentry/vue'
import { isEqual } from 'lodash-es'
import type {
  RemoveConditionalIntroductionMessageInput,
  UpdateConditionalIntroductionMessageInput,
} from '@/types/schema.generated'

import FlatButton from '@/components/atoms/FlatButton.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import {
  useConditionalIntroductionMessageQuery,
  useRemoveConditionalIntroductionMessageMutation,
  useUpdateConditionalIntroductionMessageMutation,
  type UpdateConditionalIntroductionMessageMutation,
} from './SettingsPageIntroductionMessageConditionalEditView.generated'
import {
  useFetchInflowSourceConditionQuery,
  useFetchScenarioChatsQuery,
} from './IntroductionMessageForm.generated'
import IntroductionMessageConditionForm from './IntroductionMessageConditionForm.vue'
import IntroductionMessageContentForm from './IntroductionMessageContentForm.vue'
import {
  type IntroductionMessageContentFormType,
  type IntroductionMessageConditionFormType,
  validateIntroductionMessageConditionForm,
  validateIntroductionMessageContentForm,
} from './formSchema'
import { useLogger } from '@/logger/logger'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: {
      onConfirm: () => void
      onAbort: () => void
    }
  ): void
}>()

const alert = useAlert()
const logger = useLogger()
const route = useRoute('introductionMessageSettingConditionalEdit')
const router = useRouter()

const conditionalGUID = route.params.conditionalGUID

const { result: inflowSourceOptionQueryResult } =
  useFetchInflowSourceConditionQuery()

const inflowSourceOptions = computed(() => {
  return inflowSourceOptionQueryResult.value?.segmentConditionParameters.inflowSourceCondition.inflowSources.map(
    source => ({
      text: source.inflowSourceName,
      value: source.guid,
    })
  )
})

const { result: scenarioChatResult } = useFetchScenarioChatsQuery()

const scenarioChatOptions = computed(() => {
  return scenarioChatResult.value?.scenarioChats.scenarioChats.map(chat => ({
    text: chat.title,
    value: chat.guid,
  }))
})

const initialConditionFormValue = ref<IntroductionMessageConditionFormType>()
const conditionForm = ref<IntroductionMessageConditionFormType>()

const initialContentFormValue = ref<IntroductionMessageContentFormType>()
const contentForm = ref<IntroductionMessageContentFormType>()

const { result } = useConditionalIntroductionMessageQuery(
  { input: conditionalGUID },
  { fetchPolicy: 'no-cache' }
)

watchEffect(() => {
  if (!result.value) return

  const condition = result.value.conditionalIntroductionMessage.condition
  initialConditionFormValue.value = {
    inflowSourceId: condition.userRegistrationInflowSourceDefinition.guid,
  }
  conditionForm.value = { ...initialConditionFormValue.value }

  const content = result.value.conditionalIntroductionMessage.content
  switch (content?.__typename) {
    case 'ScenarioChatIntroductionMessageContent':
      initialContentFormValue.value = {
        type: 'scenarioChat',
        scenarioChatGuid: content.scenarioChatGuid,
      }
      contentForm.value = { ...initialContentFormValue.value }
      break
    case 'PlainIntroductionMessageContent':
      initialContentFormValue.value = {
        type: 'plain',
        body: content.body,
        selections: content.selections,
      }
      contentForm.value = { ...initialContentFormValue.value }
      break
  }
})

const isEdited = computed(() => {
  return (
    !isEqual(conditionForm.value, initialConditionFormValue.value) ||
    !isEqual(contentForm.value, initialContentFormValue.value)
  )
})

const isSubmitting = ref(false)
const isSubmitDisabled = computed(() => {
  if (conditionForm.value === undefined || contentForm.value === undefined) {
    return true
  }

  // 送信中もボタンを disable する
  return (
    !!validateIntroductionMessageConditionForm(conditionForm.value) ||
    !!validateIntroductionMessageContentForm(contentForm.value) ||
    isSubmitting.value
  )
})

const { mutate: updateConditionalIntroductionMessageMutation } =
  useUpdateConditionalIntroductionMessageMutation()

const handleSubmit = async () => {
  if (!conditionForm.value || !contentForm.value) {
    return
  }

  isSubmitting.value = true

  const input: UpdateConditionalIntroductionMessageInput = {
    guid: conditionalGUID,
    condition: {
      userRegistrationInflowSourceDefinitionGUID:
        conditionForm.value?.inflowSourceId,
    },
    content:
      contentForm.value.type === 'scenarioChat'
        ? {
            scenarioChatIntroductionMessage: {
              scenarioChatGuid: contentForm.value.scenarioChatGuid,
            },
          }
        : {
            plainIntroductionMessage: {
              body: contentForm.value.body,
              selections: contentForm.value.selections,
            },
          },
  }

  try {
    logger.ltlPeople.event.updateConditionalIntroductionMessage(
      logger.buildMeta(),
      {
        data: {
          guid: conditionalGUID,
          condition: {
            userRegistrationInflowSourceDefinitionGUID:
              conditionForm.value?.inflowSourceId,
          },
          content:
            contentForm.value.type === 'scenarioChat'
              ? {
                  scenarioChatGuid: contentForm.value.scenarioChatGuid,
                }
              : {
                  body: contentForm.value.body,
                  selections: contentForm.value.selections,
                },
        },
      }
    )

    const payload = await updateConditionalIntroductionMessageMutation({
      input: input,
    })

    if (payload?.errors || !payload?.data) {
      throw new Error('useUpdateConditionalIntroductionMessageMutation failed')
    }

    defineResponseHandlers<
      UpdateConditionalIntroductionMessageMutation['updateConditionalIntroductionMessage'],
      void
    >({
      AlreadyExistsConditionalIntroductionMessageError: () => {
        isSubmitting.value = false
        alert.error('この友だち追加経路は既に使用されています。')
      },
      SaveIntroductionMessageSuccess: () => {
        // 成功時はisSubmitting.valueをtrueのままにして確認ダイアログが表示されないようにする
        // eslint-disable-next-line fp/no-mutating-methods
        router.push({ name: 'introductionMessageSetting' })
        alert.success('友だち追加経路別初期チャットメッセージを更新しました。')
      },
    })(payload.data.updateConditionalIntroductionMessage)
  } catch (error) {
    captureException(error)
    alert.error('エラーが発生しました。')
    isSubmitting.value = false
  }
}

const { mutate: removeConditionalIntroductionMessageMutation } =
  useRemoveConditionalIntroductionMessageMutation()

const isDeleting = ref(false)
const handleDelete = async () => {
  if (!window.confirm('本当にこの設定を削除しますか？')) {
    return
  }

  isDeleting.value = true

  const input: RemoveConditionalIntroductionMessageInput = {
    guid: conditionalGUID,
  }

  try {
    logger.ltlPeople.event.removeConditionalIntroductionMessage(
      logger.buildMeta(),
      {
        data: input,
      }
    )

    const payload = await removeConditionalIntroductionMessageMutation({
      input: input,
    })

    if (payload?.errors || !payload?.data) {
      throw new Error('useRemoveConditionalIntroductionMessageMutation failed')
    }

    // eslint-disable-next-line fp/no-mutating-methods
    router.push({ name: 'introductionMessageSetting' })
    alert.success('友だち追加経路別初期チャットメッセージを削除しました。')
  } catch (error) {
    captureException(error)
    alert.error('エラーが発生しました。')
    isDeleting.value = false
  }
}

onBeforeRouteLeave((_to, _from, next) => {
  if (!isEdited.value || isSubmitting.value || isDeleting.value) {
    next()
    return
  }

  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      next()
    },
    onAbort: () => {},
  })
})
</script>

<style lang="scss" module>
.introductionMessageForm {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.button {
  &:nth-child(n + 2) {
    margin-left: 16px;
  }
}
</style>
